/**
 * Ephemeral, session-only public/privacy-mode flag shared across pages.
 *
 * NOT in IndexedDB — the spec demands the flag reset to OFF on every
 * page reload so the fail-safe is "showing notifications" (see the
 * JSDoc on `ReflectoStorage.publicMode`, which reserves the storage key
 * for a future cross-tab sync and explicitly documents the INTENDED
 * shape as "in-memory only, useState at the app root" — current code
 * paths must not read or write that storage key).
 *
 * `App.tsx` mounts exactly one of `MainPage` / `SettingsPage` at a time
 * (see its own "SSE lifecycle is owned at the router level" comment for
 * the same class of problem) — a plain component-local `useState` in
 * `MainPage` can't be read from `SettingsPage`'s `SafetyNumberCard`
 * masking once the user navigates away. This module-scope store is the
 * "app root" the storage JSDoc describes, without threading a prop
 * through `App.tsx` into both pages. Mirrors `services/clipsStore.ts`'s
 * module-scope + `useSyncExternalStore` shape.
 */

let publicMode = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Current value. Stable between mutations for `useSyncExternalStore`. */
export function getPublicMode(): boolean {
  return publicMode;
}

/** Flip the flag and notify subscribers. Stable function identity — safe as a bare callback prop. */
export function togglePublicMode(): void {
  publicMode = !publicMode;
  emit();
}

/** `useSyncExternalStore` subscribe — returns an unsubscribe fn. */
export function subscribePublicMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reset to the OFF default. Exported for the test reset — mirrors `clearClips()` in `clipsStore.ts`. */
export function resetPublicMode(): void {
  if (!publicMode) return;
  publicMode = false;
  emit();
}
